import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserCog, Sparkles, Bot, Shield, LogOut, Languages, Check, ChevronDown } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { LANGUAGES } from "@/i18n";
import { cn } from "@/lib/utils";
import useAiCredit from "@/hooks/useAiCredit";
import { formatCredit } from "@/components/AiCreditBadge";
import ActionSheet, { ActionRow, ActionDivider } from "@/components/mobile/ActionSheet";

/**
 * "Compte" sheet opened from the bottom tab bar: identity, profile & billing,
 * language, admin entry and sign-out. Replaces the desktop avatar dropdown.
 */
export default function AccountSheet({ open, onOpenChange, onOpenProfile, onOpenUpgrade }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  // Only fetched while the sheet is open — the tab bar stays mounted on every root screen.
  const aiUsage = useAiCredit(open);

  const close = () => onOpenChange(false);

  const header = (
    <div className="flex items-center gap-3 px-3 pb-3 pt-1">
      {user?.picture ? (
        <img src={user.picture} alt="" className="h-12 w-12 rounded-full object-cover" />
      ) : (
        <div className="grid h-12 w-12 place-items-center rounded-full bg-neutral-950 text-lg font-semibold text-white">
          {user?.name?.[0]?.toUpperCase() || "?"}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-lg font-black tracking-tight">{user?.name}</div>
        <div className="truncate text-xs text-neutral-500">{user?.email}</div>
      </div>
      {user?.is_pro && (
        <span className="inline-flex items-center gap-1 rounded-full border border-[#E05236] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#E05236]">
          <Sparkles className="h-3 w-3" />
          Pro
        </span>
      )}
    </div>
  );

  return (
    <ActionSheet
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setLangOpen(false);
      }}
      title={t("mobile.account.title", "Mon compte")}
      header={header}
      testId="account-sheet"
    >
      {!user?.is_pro && (
        <ActionRow
          icon={Sparkles}
          accent
          testId="account-upgrade"
          onSelect={() => {
            close();
            onOpenUpgrade();
          }}
        >
          {t("dashboard.upgradeToPro")}
        </ActionRow>
      )}
      <ActionRow
        icon={UserCog}
        testId="account-profile"
        chevron
        onSelect={() => {
          close();
          onOpenProfile();
        }}
      >
        {t("mobile.account.profile", "Profil & abonnement")}
      </ActionRow>
      {aiUsage && (
        <ActionRow
          icon={Bot}
          testId="account-ai-credit"
          chevron
          onSelect={() => {
            close();
            onOpenProfile();
          }}
          trailing={
            <span className="ml-2 text-sm font-semibold tabular-nums text-neutral-950">
              {formatCredit(aiUsage.current_month?.credit_eur, i18n.language)}
            </span>
          }
        >
          {t("aiCredit.menuLabel")}
        </ActionRow>
      )}

      {/* Language: inline accordion */}
      <ActionRow
        icon={Languages}
        testId="account-language"
        onSelect={() => setLangOpen((v) => !v)}
        trailing={
          <span className="ml-2 flex items-center gap-1.5 text-sm text-neutral-500">
            <span>{current.flag}</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", langOpen && "rotate-180")} />
          </span>
        }
      >
        {t("common.language")}
      </ActionRow>
      {langOpen && (
        <div className="mb-1 ml-3 grid grid-cols-2 gap-1 border-l-2 border-stone-200 pl-2">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              data-testid={`lang-option-${l.code}`}
              onClick={() => {
                i18n.changeLanguage(l.code);
                setLangOpen(false);
              }}
              className={cn(
                "flex min-h-[44px] items-center gap-2 rounded-lg px-2.5 text-left text-sm active:bg-stone-100",
                l.code === current.code ? "bg-stone-100 font-semibold" : "text-neutral-700"
              )}
            >
              <span>{l.flag}</span>
              <span className="flex-1 truncate">{l.label}</span>
              {l.code === current.code && <Check className="h-4 w-4 text-[#E05236]" />}
            </button>
          ))}
        </div>
      )}

      {user?.is_admin && (
        <ActionRow
          icon={Shield}
          testId="account-admin"
          chevron
          onSelect={() => {
            close();
            navigate("/admin");
          }}
        >
          {t("nav.admin")}
        </ActionRow>
      )}
      <ActionDivider />
      <ActionRow
        icon={LogOut}
        destructive
        testId="account-logout"
        onSelect={async () => {
          close();
          await logout();
          navigate("/");
        }}
      >
        {t("nav.logout")}
      </ActionRow>
    </ActionSheet>
  );
}
